import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { getApi } from '../api';
import type {
  ShipmentExceptionFeedback,
  ShipmentExceptionRow,
} from '../shipment-exception-types';

export type PostAccountExceptionsBulkClosedVariables = {
  accountId: number;
  exceptionIds: number[];
  feedback?: ShipmentExceptionFeedback | null;
  note?: string | null;
};

export type BulkCloseExceptionsResult = {
  data: ShipmentExceptionRow[];
  errors: Array<{ id: number; message: string }>;
};

export default function usePostAccountExceptionsBulkClosed() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      accountId,
      exceptionIds,
      feedback,
      note,
    }: PostAccountExceptionsBulkClosedVariables): Promise<BulkCloseExceptionsResult> => {
      const { api, baseURL } = getApi();
      const body: Record<string, unknown> = {
        exception_ids: exceptionIds,
      };
      if (feedback != null) body.feedback = feedback;
      if (note != null && note !== '') body.note = note;
      const res = await api.post(
        `/api/v2/accounts/${accountId}/exceptions/bulk/closed`,
        body,
        { baseURL },
      );
      return {
        data: (res.data?.data ?? []) as ShipmentExceptionRow[],
        errors: (res.data?.errors ?? []) as Array<{ id: number; message: string }>,
      };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['account-exceptions', variables.accountId],
      });
      queryClient.invalidateQueries({
        queryKey: ['account-exception-metrics', variables.accountId],
      });
      queryClient.invalidateQueries({
        queryKey: ['account-shipment-exceptions', variables.accountId],
      });
      queryClient.invalidateQueries({
        queryKey: ['account-shipments', variables.accountId],
      });
    },
  });
}
